import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, EmptyRow, FilterBar, PageHeader, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, SummaryArray, keys, useBuildingZone } from './api';

/** Past building evacuations: dates, who started them and counts. Never names. */
export function HistoryPage() {
  const zone = useBuildingZone();
  const q = useQuery({ queryKey: keys.history, queryFn: () => api.get(`${BASE}/evacuations`, { schema: SummaryArray }) });
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (q.data ?? [])
      .filter((e) => (!kind || e.kind === kind) && (!needle || e.startedLabel.toLowerCase().includes(needle)))
      .sort((a, b) => (sort === 'newest' ? b.startedAt.localeCompare(a.startedAt) : a.startedAt.localeCompare(b.startedAt)));
  }, [q.data, search, kind, sort]);
  return (
    <>
      <PageHeader title={t('srb.history.title')} description={t('srb.history.desc')} />
      <FilterBar columns="sm:grid-cols-3">
        <TextField label={t('common.search')} hint={t('srb.history.searchHint')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <SelectField label={t('srb.history.kind')} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t('srb.history.kindAll')}</option>
          <option value="real">{t('srb.evac.kind.real')}</option>
          <option value="drill">{t('srb.evac.kind.drill')}</option>
        </SelectField>
        <SelectField label={t('srb.history.sort')} value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'oldest')}>
          <option value="newest">{t('srb.history.newest')}</option>
          <option value="oldest">{t('srb.history.oldest')}</option>
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('srb.history.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('srb.history.col.started')}</Th>
              <Th>{t('srb.history.col.kind')}</Th>
              <Th>{t('srb.history.col.by')}</Th>
              <Th>{t('srb.history.col.ended')}</Th>
              <Th>{t('srb.history.col.companies')}</Th>
              <Th>{t('srb.history.col.counts')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <EmptyRow cols={6} /> : null}
            {rows.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="px-4 py-3">{formatDateTime(e.startedAt, zone)}</td>
                <td className="px-4 py-3">
                  <Badge tone={e.kind === 'real' ? 'bad' : 'neutral'}>{t(`srb.evac.kind.${e.kind}`)}</Badge>
                </td>
                <td className="px-4 py-3">
                  {t(`srb.evac.source.${e.startedSource}`)}
                  <span className="block text-xs text-slate-700">{e.startedLabel}</span>
                </td>
                <td className="px-4 py-3">{e.status === 'active' ? <Badge tone="warn">{t('srb.history.running')}</Badge> : formatDateTime(e.endedAt, zone)}</td>
                <td className="px-4 py-3">{e.companies}</td>
                <td className="px-4 py-3">{t('srb.history.counts', e.totals)}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </>
  );
}
